import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export function useCompletedActions() {
  const { user } = useAuth();
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setCompletedIds(new Set());
      setLoading(false);
      return;
    }

    const fetchCompleted = async () => {
      const { data } = await supabase
        .from("completed_actions")
        .select("action_id")
        .eq("user_id", user.id);

      if (data) {
        setCompletedIds(new Set(data.map((r) => r.action_id)));
      }
      setLoading(false);
    };

    fetchCompleted();
  }, [user]);

  const toggleCompleted = useCallback(
    async (actionId: string) => {
      if (!user) return;

      const isCompleted = completedIds.has(actionId);

      // Optimistic update
      setCompletedIds((prev) => {
        const next = new Set(prev);
        if (isCompleted) next.delete(actionId);
        else next.add(actionId);
        return next;
      });

      if (isCompleted) {
        await supabase
          .from("completed_actions")
          .delete()
          .eq("user_id", user.id)
          .eq("action_id", actionId);
      } else {
        await supabase
          .from("completed_actions")
          .insert({ user_id: user.id, action_id: actionId });
      }
    },
    [user, completedIds]
  );

  return { completedIds, toggleCompleted, loading };
}
